import { styled } from '@mui/material';
import Button from '@mui/material/Button';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';

import { AppContext } from '../../../context/app';
import { useKioskManager } from '../../../hooks/useKioskManager';
import type { Option } from '../dropdownList/DropdownList';
import { DropdownList } from '../dropdownList/DropdownList';
import Icon from '../icon/Icon';
import { IconType } from '../icon/types';
import styles from './select.module.scss';

type SelectProps = {
  options: Option[] | [];
  onChange: (value: string | number) => void;
  disabled?: boolean;
  width?: number | 'full';
  dropdownMobileSide?: 'left' | 'right';
  placeholder: string;
  selected?: Option | null;
};

const Select: React.FC<SelectProps> = ({
  options,
  disabled,
  onChange,
  width,
  dropdownMobileSide = 'left',
  placeholder,
  selected,
}) => {
  const MIN_SCROLLABLE_LENGTH = 12;

  const { setSelectedLocation } = useContext(AppContext);
  const { kiosk, kioskMode } = useKioskManager();
  // dropdown
  const dropdownRef = useRef<any | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);

  const toggleDropdown = () => setShowDropdown(!showDropdown);

  const hideDropdown = (e: any) => {
    if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
      setShowDropdown(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', hideDropdown);
    return () => document.removeEventListener('mousedown', hideDropdown);
  }, [dropdownRef]);

  // select
  const [selectedOption, setSelectedOption] = useState<Option>(options[0]);

  const handleSelectOption = (option: Option) => {
    setSelectedLocation(undefined);
    setShowDropdown(false);
    setSelectedOption(option);
    onChange(option.value);
  };

  const widthSize = useMemo(
    () => (width === 'full' ? '100%' : `${width}px`),
    [width],
  );

  useEffect(() => {
    selected && setSelectedOption(selected);
  }, [selected]);

  const SelectBtn = styled(Button)({
    position: 'relative',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    fontWeight: 400,
    justifyContent: 'space-between',
    gap: '10px',
    padding: '16px',
    paddingLeft: '24px',
    borderRadius: '32px',
    border: '1px solid #e0e0e0',
  });

  return (
    <div
      ref={dropdownRef}
      className={`${styles.container} ${
        kiosk && kioskMode && styles[kiosk.model]
      }`}
      style={{ width: widthSize }}
    >
      <SelectBtn
        className={`${styles.select} ${showDropdown && styles.focus}`}
        onClick={toggleDropdown}
        disabled={disabled}
      >
        <div className={`${styles.label} ${disabled && styles.disabled}`}>
          {disabled ? placeholder : selectedOption?.label}
        </div>
        <div className={`${styles.icon} ${disabled && styles.disabled}`}>
          <Icon icon={showDropdown ? IconType.UP : IconType.DOWN} size="full" />
        </div>
      </SelectBtn>
      {showDropdown && !disabled && (
        <DropdownList
          options={options}
          dropdownMobileSide={dropdownMobileSide}
          selectedOption={selectedOption}
          minScrollableLength={MIN_SCROLLABLE_LENGTH}
          onSelectOption={handleSelectOption}
          marginTop={4}
        />
      )}
    </div>
  );
};

export default Select;
